// 工具函数
import moment from "moment";
import { Parser, transforms } from "json2csv";

/**
 * 防抖函数 - 在事件停止触发后延迟执行函数
 * @param func 要防抖的函数
 * @param wait 延迟时间（毫秒）
 * @returns 防抖后的函数，带有 cancel 方法
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): T & { cancel: () => void } {
  let timeoutId: NodeJS.Timeout | null = null;

  const debouncedFunction = ((...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => func(...args), wait);
  }) as T & { cancel: () => void };

  debouncedFunction.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debouncedFunction;
}

/**
 * 节流函数 - 限制函数在指定时间内只能执行一次
 * @param func 要节流的函数
 * @param limit 时间限制（毫秒）
 * @returns 节流后的函数
 */
export function throttle<T extends (...args: any[]) => any>(func: T, limit: number): T {
  let inThrottle: boolean;
  return ((...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  }) as T;
}

/**
 * 生成唯一ID
 */
export function generateId(): number {
  return Date.now() + Math.floor(Math.random() * 10000);
}

/**
 * 格式化时间
 */
export function formatTime(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return date.toLocaleDateString();
}

/**
 * 本地存储工具
 */
export const storage = {
  set<T>(key: string, value: T): void {
    try {
      if (typeof window === "undefined") return;
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.warn("Failed to save to localStorage:", error);
    }
  },

  get<T>(key: string, defaultValue: T): T {
    try {
      if (typeof window === "undefined") return defaultValue;
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn("Failed to read from localStorage:", error);
      return defaultValue;
    }
  },

  remove(key: string): void {
    try {
      if (typeof window === "undefined") return;
      localStorage.removeItem(key);
    } catch (error) {
      console.warn("Failed to remove from localStorage:", error);
    }
  },

  /**
   * 检查是否支持本地存储
   */
  isSupported(): boolean {
    try {
      if (typeof window === "undefined") return false;
      const testKey = "__localStorage_test__";
      localStorage.setItem(testKey, "test");
      localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  },
};

/**
 * 样式相关工具函数
 */
export function createTransition(properties: string[], duration = 300, easing = "ease"): string {
  return properties.map((prop) => `${prop} ${duration}ms ${easing}`).join(", ");
}

/**
 * 创建CSS阴影
 */
export function createBoxShadow(
  color = "rgba(0, 0, 0, 0.1)",
  blur = 8,
  spread = 0,
  x = 0,
  y = 2
): string {
  return `${x}px ${y}px ${blur}px ${spread}px ${color}`;
}

/**
 * 缓动函数
 */
export const easingFunctions = {
  easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
  easeOut: "cubic-bezier(0, 0, 0.2, 1)",
  easeIn: "cubic-bezier(0.4, 0, 1, 1)",
  sharp: "cubic-bezier(0.4, 0, 0.6, 1)",
} as const;

/**
 * 错误处理工具
 */
export function handleError(error: Error, context: string): void {
  // eslint-disable-next-line no-console
  console.error(`Error in ${context}:`, error);
  // 在生产环境中，这里可以集成错误报告服务
  if (process.env.NODE_ENV === "production") {
    // 可以在这里添加错误报告逻辑，如 Sentry 等
  }
}

/**
 * 性能监控工具
 */
export function measurePerformance<T>(fn: () => T, label: string): T {
  const start = performance.now();
  const result = fn();
  const end = performance.now();

  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.log(`${label} took ${(end - start).toFixed(2)} milliseconds`);
  }

  return result;
}

/**
 * 字符串工具函数
 */
export function truncateText(text: string, maxLength: number, suffix = "..."): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - suffix.length) + suffix;
}

/**
 * 首字母大写
 */
export function capitalize(text: string): string {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * 驼峰转连字符
 */
export function camelToKebab(text: string): string {
  return text.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, "$1-$2").toLowerCase();
}

/**
 * 连字符转驼峰
 */
export function kebabToCamel(text: string): string {
  return text.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
}

export function formatTimeString(timeStr: string): string {
  try {
    const date = new Date(timeStr);
    if (isNaN(date.getTime())) {
      return timeStr;
    }
    // if today, show only time
    const now = new Date();
    if (
      now.getFullYear() === date.getFullYear() &&
      now.getMonth() === date.getMonth() &&
      now.getDate() === date.getDate()
    ) {
      return moment(date).format("h:mm:ss A");
    }
    // if yesterday, show "Yesterday" + time
    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);
    if (
      yesterday.getFullYear() === date.getFullYear() &&
      yesterday.getMonth() === date.getMonth() &&
      yesterday.getDate() === date.getDate()
    ) {
      return `Yesterday ${moment(date).format("h:mm:ss A")}`;
    }
    // if 7 days ago, show DDD + time
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(now.getDate() - 7);
    if (
      sevenDaysAgo.getFullYear() === date.getFullYear() &&
      sevenDaysAgo.getMonth() === date.getMonth() &&
      sevenDaysAgo.getDate() === date.getDate()
    ) {
      return moment(date).format("DDDD h:mm:ss A");
    }
    return moment(date).format("MM/DD/YYYY h:mm:ss A");
  } catch (error) {
    console.error("Failed to format time string:", timeStr, error);
    return timeStr;
  }
}

export function convertJsonToCsv<T>(data: T[], arrays?: boolean): string {
  try {
    const fields = Object.keys(data[0] || {});
    const opts = {
      fields,
      transforms: [transforms.flatten({ objects: true, arrays: arrays ?? false })],
    };
    const parser = new Parser(opts);
    return parser.parse(data);
  } catch (error) {
    console.error("Failed to convert JSON to CSV:", error);
    return "";
  }
}

export function optimizeJsonData(data: any): any {
  if (!Array.isArray(data)) {
    return data;
  } else {
    return data.map((item) => {
      const fields = Object.keys(item);
      const optimizedItem: any = {};
      fields.forEach((field) => {
        const value = item[field];
        if (Array.isArray(value) && value.length > 10) {
          optimizedItem[field] = convertJsonToCsv(value);
        } else {
          optimizedItem[field] = value;
        }
      });
      return optimizedItem;
    });
  }
}
